import React, { useState, useEffect } from 'react';
import { Save, Plus, Edit, DollarSign, Calendar, FileText } from 'lucide-react';
import { useApp } from '../App';
import { accountingService, configService } from '../services/api';

const Accounting = () => {
  const { db, setDb } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Usar la primera caja disponible como tab por defecto
    const firstCaja = db.cajas?.[0] || 'Caja Menor';
    return firstCaja.toLowerCase().replace(/\s+/g, '_');
  });
  const [dateFilter, setDateFilter] = useState({ start: '', end: '', year: new Date().getFullYear() });
  const [areaFilter, setAreaFilter] = useState('todas'); // Nuevo filtro por área
  const [paymentMethods, setPaymentMethods] = useState([]); // Estado para métodos de pago dinámicos
  
  // Cargar métodos de pago al montar el componente
  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const methods = await configService.getPaymentMethods();
        console.log('💳 Métodos de pago cargados en Accounting:', methods);
        setPaymentMethods(methods);
      } catch (error) {
        console.error('❌ Error cargando métodos de pago:', error);
        // Usar métodos por defecto si hay error
        setPaymentMethods(['efectivo', 'tarjeta', 'transferencia']);
      }
    };
    loadPaymentMethods();
  }, []);
  
  // Escuchar cambios en configuración para actualizar métodos de pago
  useEffect(() => {
    const handleConfigChange = async (event) => {
      console.log('🔄 Accounting: Cambio en configuración detectado:', event.detail);
      if (event.detail.type === 'paymentMethod') {
        try {
          const updatedMethods = await configService.getPaymentMethods();
          console.log('💳 Métodos de pago actualizados:', updatedMethods);
          setPaymentMethods(updatedMethods);
        } catch (error) {
          console.error('❌ Error actualizando métodos de pago:', error);
        }
      }
    };

    window.addEventListener('configUpdated', handleConfigChange);
    return () => window.removeEventListener('configUpdated', handleConfigChange);
  }, []);
  
  const [formData, setFormData] = useState({
    box: (() => {
      const firstCaja = db.cajas?.[0] || 'Caja Menor';
      return firstCaja.toLowerCase().replace(/\s+/g, '_');
    })(),
    type: 'ingreso',
    description: '',
    area: 'administracion'
  });

  // Estado para múltiples pagos
  const [payments, setPayments] = useState([
    { paymentMethod: 'efectivo', amount: '' }
  ]);

  const [editFormData, setEditFormData] = useState({
    id: '',
    box: 'menor',
    type: 'ingreso',
    description: '',
    amount: '',
    paymentMethod: 'efectivo',
    area: 'administracion',
    date: ''
  });

  const formatCurrency = (value) => {
    const number = value.replace(/\D/g, '');
    return number ? parseInt(number).toLocaleString('es-CO') : '';
  };

  // Verificar si un registro puede ser editado (dentro de 12 horas)
  const canEditRecord = (record) => {
    if (!record.createdAt) return true; // Si no tiene createdAt, permitir editar
    
    const createdAt = new Date(record.createdAt);
    const now = new Date();
    const hoursElapsed = (now - createdAt) / (1000 * 60 * 60); // Convertir a horas
    
    return hoursElapsed <= 12;
  };

  const openEditModal = (record) => {
    // Verificar si se puede editar
    if (!canEditRecord(record)) {
      alert('⏰ No se puede editar este registro. Han pasado más de 12 horas desde su creación.');
      return;
    }
    
    setEditingRecord(record);
    setEditFormData({
      id: record.id,
      box: record.box,
      type: record.type,
      description: record.description,
      amount: record.amount.toLocaleString('es-CO'),
      paymentMethod: record.paymentMethod,
      area: record.area,
      date: record.date.split('T')[0] // No convertir a Date para evitar problema de zona horaria
    });
    setShowEditModal(true);
  };

  const updateRecord = async () => {
    if (!editFormData.description.trim()) {
      alert('Por favor ingrese una descripción');
      return;
    }
    
    const amountNumber = parseFloat(editFormData.amount.replace(/\D/g, ''));
    if (!amountNumber || amountNumber <= 0) {
      alert('Por favor ingrese un monto válido mayor a 0');
      return;
    }

    if (!editFormData.date) {
      alert('Por favor seleccione una fecha');
      return;
    }
    
    try {
      const recordData = {
        box: editFormData.box,
        type: editFormData.type,
        description: editFormData.description,
        amount: amountNumber,
        paymentMethod: editFormData.paymentMethod,
        area: editFormData.area,
        date: editFormData.date // Ya está en formato YYYY-MM-DD, no convertir
      };
      
      console.log('🔄 Actualizando registro contable...', recordData);
      
      // Usar el ID correcto (puede ser _id del backend o id local)
      const recordId = editingRecord._id || editingRecord.id;
      
      const updatedRecord = await accountingService.updateRecord(recordId, recordData);
      console.log('✅ Registro contable actualizado:', updatedRecord);
      
      // Actualizar estado local - reemplazar el registro editado
      setDb(prevDb => ({
        ...prevDb,
        accounting: prevDb.accounting.map(record => 
          (record._id || record.id) === recordId ? { ...record, ...recordData } : record
        )
      }));
      
      setShowEditModal(false);
      setEditingRecord(null);
      setEditFormData({
        id: '',
        box: 'menor',
        type: 'ingreso',
        description: '',
        amount: '',
        paymentMethod: 'efectivo',
        area: 'administracion',
        date: ''
      });
      
      alert('✅ Registro actualizado y guardado exitosamente');
      
    } catch (error) {
      console.error('❌ Error actualizando registro:', error);
      
      // Manejar error 403 (tiempo límite excedido)
      if (error.response && error.response.status === 403) {
        alert('⏰ No se puede editar este registro. Han pasado más de 12 horas desde su creación.');
        setShowEditModal(false);
      } else {
        alert('❌ Error al actualizar el registro: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Funciones para manejar múltiples pagos
  const addPayment = () => {
    setPayments([...payments, { paymentMethod: paymentMethods[0] || 'efectivo', amount: '' }]);
  };

  const removePayment = (index) => {
    if (payments.length > 1) {
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const updatePayment = (index, field, value) => {
    const newPayments = [...payments];
    newPayments[index][field] = value;
    setPayments(newPayments);
  };

  // Calcular total de todos los pagos
  const calculateTotal = () => {
    return payments.reduce((sum, payment) => {
      const amount = parseInt(payment.amount.replace(/\D/g, '')) || 0;
      return sum + amount;
    }, 0);
  };

  const saveRecord = async () => {
    if (!formData.description.trim()) {
      alert('Por favor ingrese una descripción');
      return;
    }

    // Validar que al menos un pago tenga monto
    const hasValidPayment = payments.some(p => p.amount && parseInt(p.amount.replace(/\D/g, '')) > 0);
    if (!hasValidPayment) {
      alert('Por favor ingrese al menos un monto');
      return;
    }
    
    try {
      // Guardar cada pago como un registro separado
      const savedRecords = [];
      
      for (const payment of payments) {
        const amountNumber = parseFloat(payment.amount.replace(/\D/g, ''));
        if (amountNumber > 0) {
          // Obtener la fecha actual en formato YYYY-MM-DD sin conversión de zona horaria
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          
          const recordData = {
            box: formData.box,
            type: formData.type,
            description: `${formData.description} - ${payment.paymentMethod}`,
            amount: amountNumber,
            paymentMethod: payment.paymentMethod,
            area: formData.area,
            date: dateString
          };
          
          console.log('🔄 Guardando pago...', recordData);
          const newRecord = await accountingService.createRecord(recordData);
          savedRecords.push(newRecord);
        }
      }
      
      console.log('✅ Todos los pagos guardados:', savedRecords);
      
      // Recargar todos los registros desde el backend para tener datos completos
      const allRecords = await accountingService.getRecords();
      setDb(prevDb => ({
        ...prevDb,
        accounting: allRecords
      }));
      
      // Reset form
      setFormData({
        box: formData.box,
        type: 'ingreso',
        description: '',
        area: 'administracion'
      });
      setPayments([{ paymentMethod: paymentMethods[0] || 'efectivo', amount: '' }]);
      setShowModal(false);
      
      // Mostrar notificación de éxito con mejor styling
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center gap-2';
      notification.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span>✅ Registro contable guardado exitosamente en el servidor</span>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
    } catch (error) {
      console.error('❌ Error guardando registro contable:', error);
      alert('❌ Error al guardar el registro: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredRecords = db.accounting.filter(r => {
    // Filtrar por tipo de caja dinámico
    const recordBoxKey = r.box?.toLowerCase().replace(/\s+/g, '_');
    const isMainTab = !activeTab.startsWith('cuadre_');
    const currentBoxKey = isMainTab ? activeTab : activeTab.replace('cuadre_', '');
    
    const boxMatch = recordBoxKey === currentBoxKey;
    
    // Filtrar por área (comparación case-insensitive)
    const areaMatch = areaFilter === 'todas' || r.area?.toLowerCase() === areaFilter;
    
    // Filtrar por fecha y año (comparación solo de fechas, sin horas)
    let dateMatch = true;
    
    // Parsear la fecha sin conversión de zona horaria
    const datePart = r.date.split('T')[0];
    const [year, month, day] = datePart.split('-');
    const recordDateOnly = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const recordYear = parseInt(year);
    
    // Filtrar por año
    if (dateFilter.year) {
      dateMatch = recordYear === parseInt(dateFilter.year);
    }
    
    // Filtrar por rango de fechas (comparar solo fechas, sin horas)
    if (dateMatch && dateFilter.start) {
      const [startYear, startMonth, startDay] = dateFilter.start.split('-');
      const startDateOnly = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
      dateMatch = dateMatch && recordDateOnly >= startDateOnly;
    }
    if (dateMatch && dateFilter.end) {
      const [endYear, endMonth, endDay] = dateFilter.end.split('-');
      const endDateOnly = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
      dateMatch = dateMatch && recordDateOnly <= endDateOnly;
    }
    
    return boxMatch && areaMatch && dateMatch;
  });

  const totals = filteredRecords.reduce((acc, record) => {
    if (record.type === 'ingreso') {
      acc.ingresos += record.amount;
    } else {
      acc.egresos += record.amount;
    }
    return acc;
  }, { ingresos: 0, egresos: 0 });

  const renderTabContent = () => {
    // Verificar si es un tab de cuadre
    const isCuadreTab = activeTab.startsWith('cuadre_');
    
    if (isCuadreTab) {
      const boxKey = activeTab.replace('cuadre_', '');
      let boxRecords = db.accounting.filter(r => {
        const recordBoxKey = r.box?.toLowerCase().replace(/\s+/g, '_');
        return recordBoxKey === boxKey;
      });
      
      // Aplicar filtro por área si está seleccionado (comparación case-insensitive)
      if (areaFilter !== 'todas') {
        boxRecords = boxRecords.filter(r => r.area?.toLowerCase() === areaFilter);
      }
      
      // Aplicar filtro por año y fecha (solo fechas, sin horas)
      boxRecords = boxRecords.filter(r => {
        // Parsear la fecha sin conversión de zona horaria
        const datePart = r.date.split('T')[0];
        const [year, month, day] = datePart.split('-');
        const recordDateOnly = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        const recordYear = parseInt(year);
        
        // Filtrar por año
        if (dateFilter.year && recordYear !== parseInt(dateFilter.year)) {
          return false;
        }
        
        // Filtrar por rango de fechas (comparar solo fechas)
        if (dateFilter.start) {
          const [startYear, startMonth, startDay] = dateFilter.start.split('-');
          const startDateOnly = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
          if (recordDateOnly < startDateOnly) return false;
        }
        if (dateFilter.end) {
          const [endYear, endMonth, endDay] = dateFilter.end.split('-');
          const endDateOnly = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
          if (recordDateOnly > endDateOnly) return false;
        }
        
        return true;
      });
      
      const boxTotals = boxRecords.reduce((acc, record) => {
        if (record.type === 'ingreso') {
          acc.ingresos += record.amount;
        } else {
          acc.egresos += record.amount;
        }
        return acc;
      }, { ingresos: 0, egresos: 0 });

      // Generar totales dinámicos basados en métodos de pago configurados
      const totalesPorMetodo = {};
      paymentMethods.forEach(method => {
        // Normalizar nombre del método (puede venir como string o objeto)
        const methodName = typeof method === 'string' ? method : method.name || method.toString();
        totalesPorMetodo[methodName.toLowerCase()] = { ingresos: 0, egresos: 0 };
      });

      // Si no hay métodos configurados, usar los por defecto
      if (paymentMethods.length === 0) {
        totalesPorMetodo.efectivo = { ingresos: 0, egresos: 0 };
        totalesPorMetodo.tarjeta = { ingresos: 0, egresos: 0 };
        totalesPorMetodo.transferencia = { ingresos: 0, egresos: 0 };
      }

      boxRecords.forEach(record => {
        const paymentMethod = record.paymentMethod?.toLowerCase();
        if (paymentMethod && totalesPorMetodo[paymentMethod]) {
          if (record.type === 'ingreso') {
            totalesPorMetodo[paymentMethod].ingresos += record.amount;
          } else {
            totalesPorMetodo[paymentMethod].egresos += record.amount;
          }
        }
      });

      return (
        <div className="space-y-6">
          {/* Filtros para cuadres */}
          <div className="bg-white p-4 rounded-lg border">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Área:</label>
                <select
                  value={areaFilter}
                  onChange={(e) => setAreaFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="todas">Todas las áreas</option>
                  {(db.areas || ['Administración', 'Ventas', 'Producción', 'Logística']).map(area => (
                    <option key={area} value={area.toLowerCase()}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Registros incluidos: {boxRecords.length}</span>
                {areaFilter !== 'todas' && (
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                    Filtrado por {areaFilter}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Cuadre de {(db.cajas || ['Caja Menor', 'Caja Mayor']).find(caja => 
                  caja.toLowerCase().replace(/\s+/g, '_') === boxKey
                ) || 'Caja'}
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-green-600 font-medium">Total Ingresos</p>
                <p className="text-2xl font-bold text-green-700">
                  ${boxTotals.ingresos.toLocaleString('es-CO')}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-red-600 font-medium">Total Egresos</p>
                <p className="text-2xl font-bold text-red-700">
                  ${boxTotals.egresos.toLocaleString('es-CO')}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-blue-600 font-medium">Saldo Final</p>
                <p className={`text-2xl font-bold ${(boxTotals.ingresos - boxTotals.egresos) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  ${(boxTotals.ingresos - boxTotals.egresos).toLocaleString('es-CO')}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-purple-600 font-medium">Transacciones</p>
                <p className="text-2xl font-bold text-purple-700">
                  {boxRecords.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h4 className="text-lg font-semibold mb-4">
              Cuadre por Método de Pago 
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({paymentMethods.length} método{paymentMethods.length !== 1 ? 's' : ''} configurado{paymentMethods.length !== 1 ? 's' : ''})
              </span>
            </h4>
            
            {Object.keys(totalesPorMetodo).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DollarSign size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No hay métodos de pago configurados.</p>
                <p className="text-sm">Agrega métodos de pago en <strong>Configuración</strong> para ver los cuadres aquí.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(totalesPorMetodo).map(([metodo, totales]) => {
                const saldo = totales.ingresos - totales.egresos;
                const transacciones = boxRecords.filter(r => r.paymentMethod === metodo).length;
                
                return (
                  <div key={metodo} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-3 h-3 rounded-full ${
                        metodo === 'efectivo' ? 'bg-green-500' :
                        metodo === 'tarjeta' ? 'bg-blue-500' :
                        metodo === 'transferencia' ? 'bg-purple-500' :
                        metodo === 'credito' ? 'bg-orange-500' :
                        metodo === 'cheque' ? 'bg-yellow-500' :
                        'bg-gray-500'
                      }`}></div>
                      <h5 className="font-semibold capitalize">{metodo}</h5>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Ingresos:</span>
                        <span className="text-green-600 font-medium">
                          ${totales.ingresos.toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Egresos:</span>
                        <span className="text-red-600 font-medium">
                          ${totales.egresos.toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-medium">Saldo:</span>
                        <span className={`font-bold ${saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${saldo.toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 text-center mt-2">
                        {transacciones} movimiento{transacciones !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-600 font-medium">Total Ingresos</p>
            <p className="text-2xl font-bold text-green-700">
              ${totals.ingresos.toLocaleString('es-CO')}
            </p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-600 font-medium">Total Egresos</p>
            <p className="text-2xl font-bold text-red-700">
              ${totals.egresos.toLocaleString('es-CO')}
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-600 font-medium">Balance</p>
            <p className="text-2xl font-bold text-blue-700">
              ${(totals.ingresos - totals.egresos).toLocaleString('es-CO')}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-4 text-gray-600 font-medium">Fecha</th>
                <th className="text-left p-4 text-gray-600 font-medium">Tipo</th>
                <th className="text-left p-4 text-gray-600 font-medium">Descripción</th>
                <th className="text-left p-4 text-gray-600 font-medium">Método</th>
                <th className="text-left p-4 text-gray-600 font-medium">Área</th>
                <th className="text-right p-4 text-gray-600 font-medium">Monto</th>
                <th className="text-center p-4 text-gray-600 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center p-8 text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <DollarSign size={48} className="text-gray-300" />
                      <span>No hay registros en esta caja</span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRecords.map(record => (
                  <tr key={record.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-4 text-gray-800">
                      {(() => {
                        const datePart = record.date.split('T')[0];
                        const [year, month, day] = datePart.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        return date.toLocaleDateString('es-CO');
                      })()}
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        record.type === 'ingreso' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {record.type}
                      </span>
                    </td>
                    <td className="p-4 text-gray-800">{record.description}</td>
                    <td className="p-4 text-gray-600 capitalize">{record.paymentMethod}</td>
                    <td className="p-4 text-gray-600 capitalize">{record.area}</td>
                    <td className={`p-4 text-right font-semibold ${
                      record.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${record.amount.toLocaleString('es-CO')}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {canEditRecord(record) ? (
                          <button
                            onClick={() => openEditModal(record)}
                            className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded hover:bg-blue-50"
                            title="Editar registro"
                          >
                            <Edit size={16} />
                          </button>
                        ) : (
                          <button
                            disabled
                            className="text-gray-300 cursor-not-allowed p-1 rounded"
                            title="⏰ No se puede editar (más de 12 horas desde creación)"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        <button
                          className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-50"
                          title="Ver detalles"
                        >
                          <FileText size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800">Módulo de Contabilidad</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <select
              value={dateFilter.year}
              onChange={(e) => setDateFilter({...dateFilter, year: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm font-semibold"
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
            <input
              type="date"
              value={dateFilter.start}
              onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              placeholder="Fecha inicio"
            />
            <span className="text-gray-500">a</span>
            <input
              type="date"
              value={dateFilter.end}
              onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              placeholder="Fecha fin"
            />
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            Nuevo Registro
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 border-b overflow-x-auto">
            {(db.cajas || ['Caja Menor', 'Caja Mayor']).map((caja, index) => {
              const cajaKey = caja.toLowerCase().replace(/\s+/g, '_');
              return [
                {
                  key: cajaKey,
                  label: caja
                },
                {
                  key: `cuadre_${cajaKey}`,
                  label: `Cuadre ${caja}`
                }
              ];
            }).flat().map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setAreaFilter('todas'); // Resetear filtro al cambiar pestaña
                }}
                className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Filtro por áreas */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Filtrar por área:</label>
              <select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="todas">Todas las áreas</option>
                {(db.areas || ['Administración', 'Ventas', 'Producción']).map(area => (
                  <option key={area} value={area.toLowerCase()}>
                    {area}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Filtros adicionales para pestañas de caja (no para cuadres) */}
        {(activeTab === 'menor' || activeTab === 'mayor') && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Filtrar por área:</label>
                <select
                  value={areaFilter}
                  onChange={(e) => setAreaFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="todas">Todas las áreas</option>
                  {(db.areas || ['Administración', 'Ventas', 'Producción', 'Logística']).map(area => (
                    <option key={area} value={area.toLowerCase()}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Mostrando {filteredRecords.length} registro{filteredRecords.length !== 1 ? 's' : ''}</span>
                {areaFilter !== 'todas' && (
                  <button
                    onClick={() => setAreaFilter('todas')}
                    className="text-purple-600 hover:text-purple-800 underline"
                  >
                    Limpiar filtro
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {renderTabContent()}
      </div>

      {/* Modal de Nuevo Registro */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-6">Nuevo Registro Contable</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Caja</label>
                <select
                  value={formData.box}
                  onChange={(e) => handleInputChange('box', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                >
                  {(db.cajas || ['Caja Menor', 'Caja Mayor']).map(caja => (
                    <option key={caja} value={caja.toLowerCase().replace(/\s+/g, '_')}>
                      {caja}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Transacción</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className={`w-full p-3 border rounded-lg font-semibold text-white transition-colors ${
                    formData.type === 'ingreso' 
                      ? 'bg-green-500 border-green-600' 
                      : 'bg-red-500 border-red-600'
                  }`}
                >
                  <option value="ingreso">Ingreso</option>
                  <option value="egreso">Egreso</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descripción *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Ingrese una descripción"
                />
              </div>

              {/* Múltiples Métodos de Pago */}
              <div className="col-span-2">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">Métodos de Pago *</label>
                  <button
                    type="button"
                    onClick={addPayment}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Agregar Método
                  </button>
                </div>
                
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  {payments.map((payment, index) => (
                    <div key={index} className="flex gap-3 items-center bg-white p-3 rounded border">
                      <div className="flex-1">
                        <select
                          value={payment.paymentMethod}
                          onChange={(e) => updatePayment(index, 'paymentMethod', e.target.value)}
                          className="w-full p-2 border rounded"
                        >
                          {(db.paymentMethods || ['Efectivo', 'Tarjeta', 'Transferencia']).map(method => (
                            <option key={method} value={method.toLowerCase()}>
                              {method}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                          <input
                            type="text"
                            value={payment.amount}
                            onChange={(e) => updatePayment(index, 'amount', formatCurrency(e.target.value))}
                            className="w-full pl-6 pr-3 py-2 border rounded"
                            placeholder="0"
                          />
                        </div>
                      </div>
                      {payments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePayment(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  
                  <div className="flex justify-between items-center pt-2 border-t-2 border-gray-300">
                    <span className="font-bold text-gray-700">Total:</span>
                    <span className="font-bold text-lg text-green-600">
                      ${calculateTotal().toLocaleString('es-CO')}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Área</label>
                <select
                  value={formData.area}
                  onChange={(e) => handleInputChange('area', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                >
                  {(db.areas || ['Administración', 'Ventas', 'Producción', 'Logística']).map(area => (
                    <option key={area} value={area.toLowerCase()}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={saveRecord}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  Guardar Registro
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setFormData({
                      box: 'menor',
                      type: 'ingreso',
                      description: '',
                      amount: '',
                      paymentMethod: 'efectivo',
                      area: 'administracion'
                    });
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-6">Editar Registro Contable</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Fecha *</label>
                <input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => handleEditInputChange('date', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Transacción</label>
                <select
                  value={editFormData.type}
                  onChange={(e) => handleEditInputChange('type', e.target.value)}
                  className={`w-full p-3 border rounded-lg font-semibold text-white transition-colors ${
                    editFormData.type === 'ingreso' 
                      ? 'bg-green-500 border-green-600' 
                      : 'bg-red-500 border-red-600'
                  }`}
                >
                  <option value="ingreso">Ingreso</option>
                  <option value="egreso">Egreso</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descripción *</label>
                <input
                  type="text"
                  value={editFormData.description}
                  onChange={(e) => handleEditInputChange('description', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Ingrese una descripción"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Monto (COP) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">$</span>
                  <input
                    type="text"
                    value={editFormData.amount}
                    onChange={(e) => handleEditInputChange('amount', formatCurrency(e.target.value))}
                    className="w-full pl-8 pr-3 py-3 border rounded-lg"
                    placeholder="0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Formato: Pesos Colombianos</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Método de Pago</label>
                <select
                  value={editFormData.paymentMethod}
                  onChange={(e) => handleEditInputChange('paymentMethod', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                >
                  {(db.paymentMethods || ['Efectivo', 'Tarjeta', 'Transferencia']).map(method => (
                    <option key={method} value={method.toLowerCase()}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Área</label>
                <select
                  value={editFormData.area}
                  onChange={(e) => handleEditInputChange('area', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                >
                  {(db.areas || ['Administración', 'Ventas', 'Producción', 'Logística']).map(area => (
                    <option key={area} value={area.toLowerCase()}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={updateRecord}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  Guardar Cambios
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingRecord(null);
                    setEditFormData({
                      id: '',
                      box: 'menor',
                      type: 'ingreso',
                      description: '',
                      amount: '',
                      paymentMethod: 'efectivo',
                      area: 'administracion',
                      date: ''
                    });
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounting;